import axios from "axios";
import { API_URL, BUCKET_URL } from "../config/config";
import { getHeaderOptions } from "./getHeaderOptions";
import logger from "../utils/logger";

const API_ENDPOINT = API_URL + "api/v1";

/**
 * Download file through backend API (if S3 CORS is not configured)
 * Add endpoint to your backend: GET /api/v1/content/download/{contentId}
 */
export const downloadContentFileViaBackend = async (
  contentId: string,
  fileName: string,
  retries: number = 2
): Promise<Blob> => {
  try {
    logger.log(`📥 Downloading via backend: ${fileName}`);
    
    let lastError: any;
    
    for (let attempt = 1; attempt <= (retries + 1); attempt++) {
      try {
        const headers = {
          ...getHeaderOptions,
          Authorization: localStorage.getItem("auth_token") || "",
        };
        
        const response = await axios.get(
          `${API_ENDPOINT}/content/${contentId}/download`,
          {
            responseType: 'blob',
            headers,
            timeout: 30000
          }
        );
        
        if (!response.data || response.data.size === 0) {
          logger.warn(`⚠️ Empty blob on attempt ${attempt}/${retries + 1}`);
          if (attempt < retries + 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          throw new Error('Received empty file from backend');
        }
        
        logger.log(`✅ Downloaded via backend: ${fileName} (${response.data.size} bytes)`);
        return response.data;
        
      } catch (attemptError: any) {
        lastError = attemptError;
        const isNetworkError = !attemptError.response || attemptError.code === 'ECONNABORTED';
        
        if (isNetworkError && attempt < retries + 1) {
          logger.warn(`⚠️ Network error on attempt ${attempt}, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }
        throw attemptError;
      }
    }
    
    throw lastError;
    
  } catch (error: any) {
    logger.error(`❌ Backend download failed for ${fileName}:`, error.message);
    throw new Error(`Failed to download ${fileName} via backend: ${error.message}`);
  }
};

export const getAllContent = (params: any) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.get(`${API_ENDPOINT}/content`, { headers: obj, params: params });
};

export const getContentById = (id: string) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.get(`${API_ENDPOINT}/content/${id}`, { headers: obj });
};

export const deleteContent = (id: string) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.delete(`${API_ENDPOINT}/content/${id}`, { headers: obj });
};

export const createNewContent = (data: any) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.post(`${API_ENDPOINT}/content`, data, { headers: obj });
};

export const updateContent = (data: any, id: string) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.put(`${API_ENDPOINT}/content/${id}`, data, { headers: obj });
};

export const getContentByType = (type: string, options?: { search?: string, page?: number, perPage?: number }) => {
  const params: any = { type };

  if (options) {
    if (options.search) params.search = options.search;
    if (options.page) params.page = options.page;
    if (options.perPage) params.perPage = options.perPage;
  }

  return getAllContent(params);
};



/**
 * Download content file using the exact filePath from API - NO MODIFICATIONS
 * @param filePath - Complete S3 path like "content/68526f3216a3da763228b32d/1751546176694.jpg" 
 * @param fileName - Display name for the downloaded file (NOT used in URL construction)
 */
export const downloadContentFile = async (
  filePath: string, 
  fileName: string,
  retries: number = 2
): Promise<Blob> => {
  try {
    logger.log(`📥 Downloading: ${fileName}`);
    logger.log(`🛤️ File path received: ${filePath}`);
    
    // 🔥 CRITICAL: Validate filePath format but DON'T modify it
    if (!filePath || !filePath.startsWith('content/')) {
      throw new Error(`Invalid filePath format: "${filePath}". Expected: content/contentId/timestamp.ext`);
    }
    
    // 🔥 CRITICAL: Use filePath AS-IS, no modifications
    const fileUrl = `${BUCKET_URL}${filePath}`;
    logger.log(`🔗 Final URL: ${fileUrl}`);
    
    let lastError: any;
    
    // Retry logic for network failures
    for (let attempt = 1; attempt <= (retries + 1); attempt++) {
      try {
        const response = await axios.get(fileUrl, {
          responseType: 'blob',
          timeout: 30000, // Increased timeout from 15s to 30s
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        // ✅ Validate blob received
        if (!response.data || response.data.size === 0) {
          logger.warn(`⚠️ Empty blob received on attempt ${attempt}/${retries + 1}, retrying...`);
          if (attempt < retries + 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
            continue;
          }
          throw new Error('Received empty file from server');
        }
        
        // ✅ Validate blob type
        if (!(response.data instanceof Blob)) {
          throw new Error(`Expected Blob but received ${typeof response.data}`);
        }
        
        logger.log(`✅ Downloaded: ${fileName} (${response.data.size} bytes) on attempt ${attempt}`);
        return response.data;
        
      } catch (attemptError: any) {
        lastError = attemptError;
        
        const isNetworkError = !attemptError.response || 
                               attemptError.code === 'ECONNABORTED' ||
                               attemptError.message?.includes('ERR_FAILED') ||
                               attemptError.message?.includes('Network Error');
        
        if (isNetworkError && attempt < retries + 1) {
          logger.warn(`⚠️ Network error on attempt ${attempt}/${retries + 1}, retrying in ${1000 * attempt}ms...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }
        
        throw attemptError;
      }
    }
    
    throw lastError || new Error('Download failed after retries');
    
  } catch (error: any) {
    const isNetworkError = error.message?.includes('ERR_FAILED') || 
                          error.message?.includes('Network Error') ||
                          !error.response;
    
    logger.error(`❌ Download failed for ${fileName}:`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      receivedFilePath: filePath,
      finalUrl: `${BUCKET_URL}${filePath}`,
      error: error.message,
      isNetworkError,
      code: error.code
    });
    
    if (isNetworkError) {
      throw new Error(`Failed to download ${fileName}: Network error (possible CORS or S3 issue). Please check that S3 bucket has proper CORS headers configured.`);
    }
    
    throw new Error(`Failed to download ${fileName}: ${error.response?.status === 404 ? 'File not found in S3' : error.message}`);
  }
};
/**
 * Convert downloaded blob to File object for upload system
 * @param blob - The downloaded blob
 * @param fileName - Original file name
 * @returns File object ready for upload
 */
export const contentBlobToFile = (blob: Blob, fileName: string): File => {
  // Detect MIME type from file extension for proper file handling
  const extension = fileName.split('.').pop()?.toLowerCase();
  let mimeType = 'application/octet-stream'; // Default fallback
  
  // Map common file extensions to MIME types
  const mimeMap: {[key: string]: string} = {
    'pdf': 'application/pdf',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg', 
    'png': 'image/png',
    'mp4': 'video/mp4',
    '3gp': 'video/3gpp',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'txt': 'text/plain'
  };
  
  if (extension && mimeMap[extension]) {
    mimeType = mimeMap[extension];
  }
  
  // Create File object with correct MIME type
  const file = new File([blob], fileName, { type: mimeType });
  
  logger.log(`📄 Created File object: ${fileName} (${file.size} bytes, ${mimeType})`);
  
  return file;
};